/* ET editorial: progressive navigation, media and existing consent-gated et_* events.
   No network requests, third-party libraries, cookies or persistent identifiers. */
(() => {
  'use strict';

  const cleanHeading = (text) => text
    .replace(/[\p{Extended_Pictographic}\p{Emoji_Presentation}\uFE0F\u200D]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();

  const slug = (text) => text.normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'section';

  const lifecycle = () => {
    const cleanups = [];
    return {
      on(target, type, handler, options) {
        target.addEventListener(type, handler, options);
        cleanups.push(() => target.removeEventListener(type, handler, options));
      },
      add(cleanup) { cleanups.push(cleanup); },
      destroy() { cleanups.splice(0).reverse().forEach((cleanup) => cleanup()); },
    };
  };

  const analyticsAllowed = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      if (typeof privacy?.analyticsProcessingAllowed === 'function') {
        return privacy.analyticsProcessingAllowed() === true;
      }
      // Compatibility only for an existing legacy API; never override a granular denial.
      return typeof privacy?.userCanBeTracked === 'function' && privacy.userCanBeTracked() === true;
    } catch { return false; }
  };

  const safeUrl = (value) => {
    if (typeof value !== 'string' || !value.trim()) return undefined;
    try {
      const url = new URL(value, window.location.href);
      if (!/^https?:$/.test(url.protocol)) return undefined;
      // Query strings and fragments can contain customer input; neither is sent.
      return `${url.origin}${url.pathname}`;
    } catch { return undefined; }
  };

  const numericId = (value) => {
    if (!/^\d+$/.test(String(value || ''))) return undefined;
    const id = Number(value);
    return Number.isSafeInteger(id) && id > 0 ? id : undefined;
  };

  const visiblyInViewport = (element) => {
    if (document.visibilityState === 'hidden') return false;
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    const width = window.innerWidth || document.documentElement.clientWidth;
    const height = window.innerHeight || document.documentElement.clientHeight;
    const visibleWidth = Math.max(0, Math.min(rect.right, width) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, height) - Math.max(rect.top, 0));
    return (visibleWidth * visibleHeight) / (rect.width * rect.height) >= .35;
  };

  const createAnalytics = (root, cleanup) => {
    const articleId = numericId(root.dataset.articleId);
    const title = root.querySelector('h1')?.textContent.trim() || '';
    let tags = [];
    try {
      const parsed = JSON.parse(root.dataset.articleTags || '[]');
      if (Array.isArray(parsed)) tags = parsed.filter((tag) => typeof tag === 'string');
    } catch { /* Missing metadata must not affect reading. */ }
    const seen = new WeakSet();
    const visibleCards = new Set();
    const cards = [...root.querySelectorAll('[data-impression]')];

    const publish = (name, payload = {}) => {
      if (!analyticsAllowed()) return false;
      const event = name.startsWith('et_') ? name : `et_${name}`;
      const data = { ts: Date.now(), page: 'blog', article_id: articleId, ...payload, consent: 'granted' };
      let accepted = false;
      try {
        if (typeof window.Shopify?.analytics?.publish === 'function') {
          const result = window.Shopify.analytics.publish(event, data);
          // A pixel failure must not break the storefront or create an unhandled rejection.
          if (result && typeof result.catch === 'function') result.catch(() => {});
          accepted = true;
        }
      } catch { /* Keep the existing independent dataLayer route available. */ }
      try {
        if (Array.isArray(window.dataLayer)) {
          window.dataLayer.push({ event, ...data });
          accepted = true;
        }
      } catch { /* Analytics must not interrupt shopper interactions. */ }
      return accepted;
    };

    const emitView = () => {
      if (window.__ET_FLAGS?.et_viewed_blog_sent) return;
      if (publish('viewed_blog', {
        article_title: title,
        article_handle: root.dataset.articleHandle || window.location.pathname.split('/').filter(Boolean).pop() || '',
        article_url: safeUrl(root.dataset.articleUrl || window.location.href),
        primary_topic: (root.dataset.primaryTopic || 'other').toLowerCase(),
        tags,
        context: { namespace: 'blog_article', location: 'view' },
      })) {
        window.__ET_FLAGS = window.__ET_FLAGS || {};
        window.__ET_FLAGS.et_viewed_blog_sent = true;
      }
    };

    const emitImpression = (card) => {
      if (seen.has(card)) return;
      if (publish('impression', {
        impression_type: card.dataset.impression,
        product_id: numericId(card.dataset.productId),
        product_variant_id: numericId(card.dataset.productVariantId),
        context: { namespace: 'blog_article', location: card.dataset.impression },
      })) seen.add(card);
    };

    const activate = () => {
      if (!analyticsAllowed()) return;
      emitView();
      // No denied-history queue: on permission changes only currently visible cards qualify.
      visibleCards.forEach((card) => {
        // Recheck geometry: a consent event can precede the latest observer delivery.
        if (visiblyInViewport(card)) emitImpression(card);
      });
    };

    if ('IntersectionObserver' in window && cards.length) {
      const observer = new IntersectionObserver((changes) => {
        changes.forEach(({ target, isIntersecting, intersectionRatio }) => {
          if (isIntersecting && intersectionRatio >= .35) visibleCards.add(target);
          else visibleCards.delete(target);
        });
        activate();
      }, { threshold: .35 });
      cards.forEach((card) => observer.observe(card));
      cleanup.add(() => observer.disconnect());
    }

    cleanup.on(root, 'click', (event) => {
      const link = event.target.closest('a[data-block-type], button[data-block-type], a[data-product-id], a[data-collection-id], .ete-product a');
      if (!link || !root.contains(link)) return;
      const card = link.closest('.ete-product');
      const type = link.dataset.blockType || (card ? 'spotlight_cta' : 'link');
      publish('click', {
        location: type,
        href: safeUrl(link.getAttribute('href')),
        label: link.textContent.trim().replace(/\s+/g, ' ').slice(0, 180),
        product_id: numericId(link.dataset.productId || card?.dataset.productId),
        collection_id: numericId(link.dataset.collectionId),
        context: { namespace: 'blog_article', location: type },
      });
    });

    root.querySelectorAll('.faq-accordion details, details.et-faq__item, .ete-faq details').forEach((disclosure) => {
      let previous = disclosure.open;
      cleanup.on(disclosure, 'toggle', () => {
        if (previous === disclosure.open) return;
        previous = disclosure.open;
        publish('faq_toggled', {
          state: disclosure.open ? 'open' : 'close',
          title: disclosure.querySelector('summary')?.textContent.trim().slice(0, 180) || '',
          context: { namespace: 'blog_article', location: 'faq' },
        });
      });
    });

    cleanup.on(document, 'visitorConsentCollected', activate);
    cleanup.on(document, 'trackingConsentAccepted', activate);
    // Existing privacy loaders may finish after this deferred script. Do not load another library.
    cleanup.on(document, 'shopifyCustomerPrivacyApiLoaded', activate);
    cleanup.on(document, 'visibilitychange', activate);
    cleanup.on(window, 'load', activate, { once: true });
    activate();
    return { publish, activate };
  };

  const currentEntry = (entries, offset) => {
    let current;
    for (const entry of entries) {
      if (entry.heading.getBoundingClientRect().top > offset + 1) break;
      current = entry;
    }
    return current;
  };

  const addHeadingId = (heading, index) => {
    if (heading.id) return heading.id;
    const stem = `ete-${slug(cleanHeading(heading.textContent))}`;
    let id = stem;
    let suffix = index + 1;
    while (document.getElementById(id)) id = `${stem}-${suffix++}`;
    heading.id = id;
    return id;
  };

  const mediaLayout = (wrapper, image, articleId) => {
    const informativeDiagram = /\b(infographic|diagram|chart)\b/i.test(image.alt || '');
    const legacyLayout = /(?:float|margin(?:-(?:top|right|bottom|left))?|(?:max-|min-)?width)\s*:/i;
    // Three verified diagrams in the legacy B12 article lack consistent descriptive alt.
    // Treat only these exact sources as wide; do not infer or replace their stored alt text.
    const legacyB12Diagram = wrapper.tagName === 'DIV' && String(articleId) === '561776721985'
      && /\/(?:Aussie_Sources|Key_Functions|Absorption)_480x480\.png(?:\?|$)/.test(image.getAttribute('src') || '');
    return {
      wide: legacyB12Diagram
        || wrapper.classList.contains('ete-image-wide')
        || wrapper.dataset.imageLayout === 'full'
        || image.classList.contains('ete-image-wide')
        || image.dataset.imageLayout === 'full'
        || wrapper.classList.contains('et-media--full')
        || informativeDiagram,
      legacy: legacyLayout.test(wrapper.getAttribute('style') || '')
        || legacyLayout.test(image.getAttribute('style') || ''),
    };
  };

  const prepareMedia = (body) => {
    const articleId = body.closest('[data-ete-article]')?.dataset.articleId;
    body.querySelectorAll('figure, p, div').forEach((wrapper) => {
      const images = wrapper.querySelectorAll('img');
      if (images.length !== 1) return;
      if (wrapper.closest('.ete-products, .ete-product, table, .micro-summary, .ms-geo')) return;
      const image = images[0];
      if (wrapper.tagName === 'DIV') {
        const parent = image.parentElement;
        const directMedia = parent === wrapper
          || (['A', 'PICTURE'].includes(parent?.tagName) && parent.parentElement === wrapper);
        // Exclude larger content containers and nested wrappers before cloning their text.
        if (!directMedia) return;
      }
      if (wrapper.tagName === 'P' || wrapper.tagName === 'DIV') {
        if (wrapper.closest('figure')) return;
        // A paragraph containing prose must remain prose, even if it also has an image.
        const copy = wrapper.cloneNode(true);
        copy.querySelectorAll('img').forEach((img) => img.remove());
        if (copy.textContent.trim()) return;
      }
      const layout = mediaLayout(wrapper, image, articleId);
      wrapper.classList.add(layout.wide ? 'ete-image-wide' : 'ete-image-wrap');
      // Override only presentational legacy layout through scoped classes, never stored styles.
      if (layout.legacy) wrapper.classList.add('ete-image-legacy');
      if (!image.hasAttribute('loading')) image.loading = 'lazy';
      if (!image.hasAttribute('decoding')) image.decoding = 'async';
      // Real source dimensions reserve the correct ratio after the first render.
      // Existing authored dimensions and source art are never replaced.
      const reserveRatio = () => {
        if (!image.hasAttribute('width') && !image.hasAttribute('height')
            && image.naturalWidth && image.naturalHeight) {
          image.setAttribute('width', String(image.naturalWidth));
          image.setAttribute('height', String(image.naturalHeight));
        }
      };
      if (image.complete) reserveRatio();
      else image.addEventListener('load', reserveRatio, { once: true });
    });
    body.querySelectorAll('table').forEach((table) => {
      let wrapper = table.parentElement;
      if (!wrapper.matches('.table-wrap, .ete-table-wrap')) {
        wrapper = document.createElement('div');
        wrapper.className = 'ete-table-wrap';
        table.before(wrapper);
        wrapper.append(table);
      }
      // A local scroll area must also work for keyboard users.
      wrapper.tabIndex = 0;
      wrapper.setAttribute('role', 'region');
      const caption = table.querySelector('caption');
      wrapper.setAttribute('aria-label', caption ? caption.textContent.trim() : 'Comparison table, scroll for more columns');
    });
  };

  const prepareTakeaways = (body) => {
    body.querySelectorAll('.micro-summary, .ms-geo').forEach((block) => {
      const heading = block.querySelector(':scope > h2, :scope > h3');
      if (!heading || !/^Key Takeaways at a Glance$/i.test(cleanHeading(heading.textContent))) return;
      if (block.querySelector(':scope > .ete-takeaways')) return;
      const disclosure = document.createElement('details');
      disclosure.className = 'ete-takeaways';
      const summary = document.createElement('summary');
      summary.append(heading);
      const content = document.createElement('div');
      content.className = 'ete-takeaways__content';
      // Keep the original content and schema attributes; only presentation changes.
      [...block.childNodes].forEach((node) => {
        if (node.nodeName !== 'META') content.append(node);
      });
      disclosure.append(summary, content);
      block.classList.add('ete-takeaways-block');
      block.append(disclosure);
    });
  };

  const init = (root) => {
    if (root.dataset.eteReady === 'true') return;
    const body = root.querySelector('.ete-body');
    if (!body) return;
    root.dataset.eteReady = 'true';
    const cleanup = lifecycle();
    prepareTakeaways(body);
    prepareMedia(body);
    createAnalytics(root, cleanup);

    const headings = [...body.querySelectorAll('h2')]
      .filter((heading) => !heading.closest('.micro-summary, .ms-geo, .ete-product, details'))
      .filter((heading) => cleanHeading(heading.textContent));
    const entries = headings.map((heading, index) => ({
      heading,
      id: addHeadingId(heading, index),
      label: cleanHeading(heading.textContent),
    }));
    headings.forEach((heading) => {
      // Native fragment navigation and script focus share the same accessible destination.
      if (!heading.hasAttribute('tabindex')) heading.tabIndex = -1;
    });

    if (entries.length) {
      root.querySelectorAll('[data-ete-toc]').forEach((nav) => {
        const list = document.createElement('ol');
        entries.forEach(({ id, label }) => {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = `#${encodeURIComponent(id)}`;
          link.textContent = label;
          item.append(link);
          list.append(item);
        });
        nav.replaceChildren(list);
      });
    }

    let frame = 0;
    let activeId;
    const links = [...root.querySelectorAll('[data-ete-toc] a')];
    const offset = parseFloat(getComputedStyle(root).getPropertyValue('--ete-anchor-offset')) || 128;
    const updateCurrent = () => {
      frame = 0;
      const id = currentEntry(entries, offset)?.id;
      if (id === activeId) return;
      activeId = id;
      links.forEach((link) => {
        if (decodeURIComponent(link.hash.slice(1)) === id) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    };
    const scheduleCurrent = () => {
      if (!frame) frame = requestAnimationFrame(updateCurrent);
    };
    const focusFragment = () => {
      let id;
      try { id = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
      const target = id && document.getElementById(id);
      if (!target || !root.contains(target)) return;
      let revealed = false;
      for (let ancestor = target.parentElement; ancestor && ancestor !== root; ancestor = ancestor.parentElement) {
        if (ancestor.tagName === 'DETAILS' && !ancestor.open) {
          const summary = ancestor.querySelector(':scope > summary');
          if (!summary?.contains(target)) {
            ancestor.open = true;
            revealed = true;
          }
        }
      }
      if (target.matches('h2, h3, h4, .ete-body')) {
        if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
        target.focus({ preventScroll: true });
      }
      if (revealed) target.scrollIntoView({ block: 'start', behavior: 'auto' });
      scheduleCurrent();
    };
    cleanup.on(root, 'click', (event) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href^="#"]');
      if (!link || !root.contains(link)) return;
      const mobileDisclosure = link.closest('.ete-toc-mobile');
      if (mobileDisclosure) mobileDisclosure.open = false;
      // Do not prevent the native hash/history update; focus follows it after default action.
      requestAnimationFrame(focusFragment);
    });
    cleanup.on(window, 'hashchange', focusFragment);
    cleanup.on(window, 'scroll', scheduleCurrent, { passive: true });
    cleanup.on(window, 'resize', scheduleCurrent, { passive: true });
    cleanup.add(() => { if (frame) cancelAnimationFrame(frame); });

    const title = root.querySelector('h1');
    if (title && /\brecipe\b/i.test(title.textContent)) {
      const recipe = entries.find(({ heading }) => heading.matches('[data-recipe-start], #recipe, #recipe-method'))
        || entries.find(({ label }) => /^(?:how to (?:make|cook|prepare)|method\b|directions\b|base recipe\b|recipe method\b)/i.test(label))
        || entries.find(({ label }) => /^ingredients(?:\s|$)/i.test(label));
      if (recipe) root.querySelectorAll('[data-ete-recipe-jump]').forEach((link) => {
        link.href = `#${encodeURIComponent(recipe.id)}`;
        link.hidden = false;
      });
    }

    updateCurrent();
    if (window.location.hash) requestAnimationFrame(focusFragment);
    cleanup.on(root, 'ete:destroy', () => {
      cleanup.destroy();
      delete root.dataset.eteReady;
    }, { once: true });
  };

  const scan = (scope = document) => {
    if (scope.matches && scope.matches('[data-ete-article]')) init(scope);
    scope.querySelectorAll('[data-ete-article]').forEach(init);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else scan();
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches('[data-ete-article]')) event.target.dispatchEvent(new Event('ete:destroy'));
    event.target.querySelectorAll('[data-ete-article]').forEach((root) => root.dispatchEvent(new Event('ete:destroy')));
  });
})();
